import { useState, useEffect } from "react"
import "./AddNewColumnPanel.css"

const AddNewColumnPanel = ({
    variant = "screening",
    onClose,
    onAdd,
    onUpdate,
    onDelete,
    editingColumn,
}) => {
    const isExtraction = variant === "extraction"
    const isEdit = Boolean(editingColumn?.id)
    const [name, setName] = useState("")
    const [secondaryText, setSecondaryText] = useState("")
    const [answerStructure, setAnswerStructure] = useState("any")
    const [initialName, setInitialName] = useState("")
    const [initialSecondaryText, setInitialSecondaryText] = useState("")
    const [initialAnswerStructure, setInitialAnswerStructure] = useState("any")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState("")

    const namePlaceholder = isExtraction
        ? "Name your extraction column"
        : "Name your screening criterion"
    const secondaryLabel = isExtraction ? "Instructions" : "Screening question"
    const secondaryOptional = isExtraction
    const secondaryId = isExtraction ? "extractionInstructions" : "screeningQuestion"
    const secondaryPlaceholder = isExtraction
        ? "Describe of what kind of data you want to extract"
        : 'If answer is "yes", paper is more likely to be screened in'

    useEffect(() => {
        if (isEdit) {
            const nextName = editingColumn?.name ?? ""
            const nextSecondary = isExtraction
                ? editingColumn?.instructions ?? ""
                : editingColumn?.screeningQuestion ?? ""
            const nextAnswerStructure = editingColumn?.answerStructure ?? "any"
            setName(nextName)
            setSecondaryText(nextSecondary)
            setAnswerStructure(nextAnswerStructure)
            setInitialName(nextName)
            setInitialSecondaryText(nextSecondary)
            setInitialAnswerStructure(nextAnswerStructure)
        } else {
            setName("")
            setSecondaryText("")
            setAnswerStructure("any")
            setInitialName("")
            setInitialSecondaryText("")
            setInitialAnswerStructure("any")
        }
        setSubmitError("")
    }, [isEdit, isExtraction, editingColumn?.id, editingColumn?.name, editingColumn?.screeningQuestion, editingColumn?.instructions, editingColumn?.answerStructure])

    const handleSubmit = async (e) => {
        e.preventDefault()
        const trimmedName = name.trim()
        const trimmedSecondary = secondaryText.trim()
        if (!trimmedName) return
        setIsSubmitting(true)
        setSubmitError("")
        try {
            if (isEdit) {
                await onUpdate({
                    columnId: editingColumn.id,
                    name: trimmedName,
                    ...(isExtraction
                        ? { instructions: trimmedSecondary, answerStructure }
                        : { screeningQuestion: trimmedSecondary }),
                })
            } else if (isExtraction) {
                await onAdd({
                    name: trimmedName,
                    answerStructure,
                    ...(trimmedSecondary ? { instructions: trimmedSecondary } : {}),
                })
            } else {
                await onAdd({ name: trimmedName, screeningQuestion: trimmedSecondary })
            }
            onClose()
        } catch (err) {
            setSubmitError(
                err?.message || (isEdit ? "Failed to update column" : "Failed to add column")
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleBack = () => {
        if (!isSubmitting) onClose()
    }

    const handleDiscard = () => {
        if (!isEdit) return
        if (isSubmitting) return
        setName(initialName)
        setSecondaryText(initialSecondaryText)
        setAnswerStructure(initialAnswerStructure)
        setSubmitError("")
    }

    const handleDelete = async () => {
        if (!isEdit || !editingColumn?.id) return

        setIsSubmitting(true)
        setSubmitError("")
        try {
            await onDelete(editingColumn.id)
            onClose()
        } catch (err) {
            setSubmitError(err?.message || "Failed to delete column")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="add-new-column-sidebar-root" role="region" aria-labelledby="add-new-column-title">
            <header className="add-new-column-header">
                <button type="button" className="add-new-column-back" onClick={handleBack}>
                    Back
                </button>
                <h2 id="add-new-column-title" className="add-new-column-title">
                    Column details
                </h2>
                <div className="add-new-column-header-spacer" aria-hidden />
            </header>

            <form className="add-new-column-form" onSubmit={handleSubmit}>
                <div className="add-new-column-field">
                    <label htmlFor="columnName" className="add-new-column-label">
                        Name
                    </label>
                    <input
                        id="columnName"
                        name="columnName"
                        type="text"
                        className="add-new-column-input"
                        placeholder={namePlaceholder}
                        autoComplete="off"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="add-new-column-field">
                    <label htmlFor={secondaryId} className="add-new-column-label">
                        {secondaryLabel}
                        {secondaryOptional && (
                            <span className="add-new-column-label-optional"> (optional)</span>
                        )}
                    </label>
                    <textarea
                        id={secondaryId}
                        name={secondaryId}
                        className="add-new-column-textarea"
                        rows={4}
                        placeholder={secondaryPlaceholder}
                        value={secondaryText}
                        onChange={(e) => setSecondaryText(e.target.value)}
                    />
                </div>

                {isExtraction && (
                    <div className="add-new-column-field">
                        <label className="add-new-column-label">Answer structure</label>
                        <div className="answer-structure-group">
                            {[
                                { value: "any", label: "Any" },
                                { value: "yes_no_maybe", label: "Yes/No/Maybe" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`answer-structure-btn${answerStructure === opt.value ? " answer-structure-btn--active" : ""}`}
                                    onClick={() => setAnswerStructure(opt.value)}
                                    disabled={isSubmitting}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {submitError && (
                    <p className="add-new-column-error" role="alert">
                        {submitError}
                    </p>
                )}

                <footer className="add-new-column-footer">
                    {isEdit ? (
                        <>
                            <button
                                type="submit"
                                className="add-new-column-btn add-new-column-btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Update"}
                            </button>
                            <button
                                type="button"
                                className="add-new-column-btn add-new-column-btn-danger"
                                onClick={handleDelete}
                                disabled={isSubmitting}
                            >
                                Delete
                            </button>
                            <button
                                type="button"
                                className="add-new-column-btn add-new-column-btn-discard"
                                onClick={handleDiscard}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="add-new-column-btn add-new-column-btn-cancel"
                                onClick={handleBack}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="add-new-column-btn add-new-column-btn-primary"
                                disabled={isSubmitting || !name.trim() || (!isExtraction && !secondaryText.trim())}
                            >
                                {isSubmitting ? "Adding..." : "Add column"}
                            </button>
                        </>
                    )}
                </footer>
            </form>
        </div>
    )
}

export default AddNewColumnPanel
